import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVoiceStore } from '@stores/voiceStore';
import { getAvailableVoices, type VoiceOption, useTextToSpeech } from '@services/voice/textToSpeech';
import { Settings, Volume2, Mic, Zap, Info } from 'lucide-react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as SwitchPrimitive from '@radix-ui/react-switch';

/**
 * Voice Settings Panel
 * Allows users to configure voice input and output preferences
 */
export default function VoiceSettingsPanel() {
  const { t } = useTranslation();
  const { preferences, capabilities, updatePreferences, resetToDefaults } = useVoiceStore();
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const { speak, stop } = useTextToSpeech();
  const [testMessage] = useState('Hello! This is a test of the text to speech system.');
  const [isTestPlaying, setIsTestPlaying] = useState(false);

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = getAvailableVoices();
      setVoices(availableVoices);
    };

    loadVoices();

    // Voices may load asynchronously
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  return (
    <div className="w-full max-w-md space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {t('voice.settings', 'Voice Settings')}
          </h3>
        </div>
        <button
          onClick={resetToDefaults}
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          {t('common.reset', 'Reset')}
        </button>
      </div>

      {/* Voice Speed Control */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
            <Volume2 className="h-4 w-4" />
            {t('voice.speed', 'Speech Speed')}
          </label>
          <span className="text-sm font-mono text-neutral-600 dark:text-neutral-400">
            {preferences.ttsSpeed.toFixed(1)}x
          </span>
        </div>
        <SliderPrimitive.Root
          className="relative flex h-5 w-full touch-none select-none items-center"
          value={[preferences.ttsSpeed]}
          onValueChange={([speed]) => {
            if (speed !== undefined) {
              updatePreferences({ ttsSpeed: speed });
            }
          }}
          min={0.5}
          max={2.0}
          step={0.1}
        >
          <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
            <SliderPrimitive.Range className="absolute h-full bg-primary-600 dark:bg-primary-400" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary-600 bg-white shadow transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:border-primary-400 dark:bg-neutral-900" />
        </SliderPrimitive.Root>
        <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400">
          <span>0.5x</span>
          <span>1.0x</span>
          <span>2.0x</span>
        </div>
      </div>

      {/* Voice Selection */}
      {voices.length > 0 && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
            <Mic className="h-4 w-4" />
            {t('voice.voice', 'Voice')}
          </label>
          <SelectPrimitive.Root
            value={preferences.ttsVoice || 'default'}
            onValueChange={(voice) => updatePreferences({ ttsVoice: voice })}
          >
            <SelectPrimitive.Trigger className="flex w-full items-center justify-between rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700">
              <SelectPrimitive.Value placeholder={t('voice.selectVoice', 'Select voice')}>
                {preferences.ttsVoice === 'default' || !preferences.ttsVoice
                  ? t('voice.defaultVoice', 'Default (Auto)')
                  : voices.find(v => v.id === preferences.ttsVoice)?.name || preferences.ttsVoice}
              </SelectPrimitive.Value>
              <SelectPrimitive.Icon>
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>
            <SelectPrimitive.Portal>
              <SelectPrimitive.Content
                className="overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800 z-50"
                position="popper"
                sideOffset={4}
              >
                <SelectPrimitive.Viewport className="max-h-64 overflow-y-auto p-1">
                  <SelectPrimitive.Item
                    value="default"
                    className="relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none hover:bg-neutral-100 focus:bg-neutral-100 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
                  >
                    <SelectPrimitive.ItemText>
                      {t('voice.defaultVoice', 'Default (Auto)')}
                    </SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                  {voices.map((voice) => (
                    <SelectPrimitive.Item
                      key={voice.id}
                      value={voice.id}
                      className="relative flex cursor-pointer select-none items-center rounded-md px-3 py-2 text-sm outline-none hover:bg-neutral-100 focus:bg-neutral-100 dark:hover:bg-neutral-700 dark:focus:bg-neutral-700"
                    >
                      <SelectPrimitive.ItemText>
                        {voice.name}{' '}
                        <span className="text-xs text-neutral-500">({voice.lang})</span>
                      </SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                  ))}
                </SelectPrimitive.Viewport>
              </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
          </SelectPrimitive.Root>
        </div>
      )}

      {/* Test TTS Button */}
      <div className="space-y-2">
        <button
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Prevent double clicks
            if (isTestPlaying) {
              console.log('⚠️ Already processing, ignoring click');
              return;
            }

            console.log('🔘 Test button clicked');
            setIsTestPlaying(true);

            try {
              // Always stop first to clear any pending speech
              console.log('🛑 Stopping any existing speech...');
              stop();

              // Wait for cleanup
              await new Promise(resolve => setTimeout(resolve, 300));

              console.log('▶️ Starting test speech...');
              await speak(testMessage);
              console.log('✅ Test speech completed');
            } catch (error) {
              console.error('❌ Test speech error:', error);
            } finally {
              setIsTestPlaying(false);
            }
          }}
          disabled={isTestPlaying}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Volume2 className="h-5 w-5" />
          {isTestPlaying ? 'Playing...' : 'Test Voice'}
        </button>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
          Click to hear: "{testMessage}"
        </p>
      </div>

      {/* Auto-play Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t('voice.autoPlay', 'Auto-play AI responses')}
          </label>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {t('voice.autoPlayDescription', 'Automatically read AI messages aloud')}
          </p>
        </div>
        <SwitchPrimitive.Root
          checked={preferences.autoPlay}
          onCheckedChange={(autoPlay) => updatePreferences({ autoPlay })}
          className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary-600 data-[state=unchecked]:bg-neutral-200 dark:data-[state=checked]:bg-primary-400 dark:data-[state=unchecked]:bg-neutral-700"
        >
          <SwitchPrimitive.Thumb className="pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0 dark:bg-neutral-900" />
        </SwitchPrimitive.Root>
      </div>

      {/* Push-to-talk Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
            <Zap className="h-4 w-4" />
            {t('voice.pushToTalk', 'Push-to-talk mode')}
          </label>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {t('voice.pushToTalkHint', 'Hold spacebar to speak')}
          </p>
        </div>
        <SwitchPrimitive.Root
          checked={preferences.pushToTalkMode}
          onCheckedChange={(pushToTalkMode) => updatePreferences({ pushToTalkMode })}
          className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary-600 data-[state=unchecked]:bg-neutral-200 dark:data-[state=checked]:bg-primary-400 dark:data-[state=unchecked]:bg-neutral-700"
        >
          <SwitchPrimitive.Thumb className="pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0 dark:bg-neutral-900" />
        </SwitchPrimitive.Root>
      </div>

      {/* Engine Status */}
      <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-800/50">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 text-neutral-500 dark:text-neutral-400" />
          <div className="space-y-1 text-xs text-neutral-600 dark:text-neutral-400">
            <div>
              <span className="font-medium">{t('voice.stt', 'Speech-to-Text')}:</span>{' '}
              {capabilities.whisperLoaded ? 'Whisper Web' : t('voice.webSpeechApi', 'Web Speech API')}
            </div>
            <div>
              <span className="font-medium">{t('voice.tts', 'Text-to-Speech')}:</span>{' '}
              {capabilities.kokoroLoaded ? 'Kokoro' : t('voice.webSpeechApi', 'Web Speech API')}
            </div>
            {capabilities.webgpu && (
              <div className="mt-2 text-green-600 dark:text-green-400">
                ✓ {t('voice.webgpuEnabled', 'WebGPU enabled')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
